<template>
  <div style="max-width: 960px; margin: 0 auto; padding: 1.25rem;">

    <!-- Header -->
    <div style="margin-bottom: 1.25rem;">
      <h1 style="font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0 0 0.25rem;">Donations</h1>
      <p style="font-size: 0.875rem; color: #6B7280; margin: 0;">
        {{ donationsData.total ? donationsData.total.toLocaleString() : '0' }} donations found
      </p>
    </div>

    <!-- Summary stats -->
    <div style="display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ donationsData.total ? donationsData.total.toLocaleString() : '0' }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Results</div>
      </div>
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ donations.length }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Loaded</div>
      </div>
      <div v-if="filterCount > 0" style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #FDF2F8; border: 1px solid #FBCFE8; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #BE185D;">{{ filterCount }}</div>
        <div style="font-size: 0.6875rem; color: #9D174D; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Filters Active</div>
      </div>
    </div>

    <!-- Search & Action bar -->
    <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem; padding: 0.875rem 1rem; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 0.625rem;">
      <div style="display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 200px;">
        <div style="position: relative; flex: 1;">
          <svg xmlns="http://www.w3.org/2000/svg" style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); width: 0.875rem; height: 0.875rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" v-model="searchTerm" v-on:keyup.enter="search" placeholder="Search by ID, name, campaign..." style="width: 100%; padding: 0.5rem 0.75rem 0.5rem 2.25rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; outline: none;" @focus="$event.target.style.borderColor='#EC4899'" @blur="$event.target.style.borderColor='#D1D5DB'" />
        </div>
        <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; white-space: nowrap;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">Search</button>
        <button @click="openFilterModal = true" style="padding: 0.5rem 1rem; background: #374151; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer; white-space: nowrap; position: relative;" @mouseover="$event.target.style.background='#1F2937'" @mouseout="$event.target.style.background='#374151'">
          Filter
          <span v-if="filterCount > 0" style="position: absolute; top: -0.375rem; right: -0.375rem; width: 1.125rem; height: 1.125rem; background: #EC4899; color: white; border-radius: 50%; font-size: 0.625rem; font-weight: 700; display: flex; align-items: center; justify-content: center;">{{ filterCount }}</span>
        </button>
      </div>
      <button @click="download" style="padding: 0.5rem 1rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.8125rem; font-weight: 500; cursor: pointer; white-space: nowrap; display: flex; align-items: center; gap: 0.375rem;" @mouseover="$event.target.style.borderColor='#10B981'; $event.target.style.color='#10B981'" @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'">
        <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.875rem; height: 0.875rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Export
      </button>
    </div>

    <!-- Results info -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; padding: 0 0.25rem;">
      <span style="font-size: 0.75rem; color: #9CA3AF;">Showing {{ donations.length }} of {{ donationsData.total || 0 }}</span>
    </div>

    <!-- Empty state -->
    <div v-if="donations.length === 0" style="text-align: center; padding: 3rem 1rem; color: #9CA3AF; background: #FAFAFA; border: 1.5px dashed #D1D5DB; border-radius: 0.75rem;">
      <p style="font-size: 0.9375rem; margin-bottom: 0.25rem; color: #6B7280; font-weight: 500;">No donations found</p>
      <p style="font-size: 0.8125rem;">Try adjusting your search or filters.</p>
    </div>

    <!-- Donation list -->
    <div v-else style="display: flex; flex-direction: column; gap: 0.5rem;">
      <div
        v-for="donation in donations"
        :key="donation.id"
        style="border-radius: 0.625rem; overflow: visible; box-shadow: 0 1px 3px rgba(0,0,0,0.08); border: 1px solid #E5E7EB; position: relative;"
      >
        <div
          :style="{ display: 'flex', alignItems: 'center', padding: '0.75rem 1rem', gap: '0.875rem', background: 'white', borderLeft: '4px solid ' + paymentColor(donation.payment_type_id) }"
          @mouseover="$event.currentTarget.style.background='#FAFAFA'"
          @mouseout="$event.currentTarget.style.background='white'"
        >
          <!-- Payment type icon -->
          <div :style="{ flexShrink: 0, width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: paymentBg(donation.payment_type_id), display: 'flex', alignItems: 'center', justifyContent: 'center' }">
            <!-- Credit Card -->
            <svg v-if="donation.payment_type_id == 1" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #EC4899;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
            <!-- Check -->
            <svg v-else-if="donation.payment_type_id == 2" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #3B82F6;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <!-- Cash -->
            <svg v-else-if="donation.payment_type_id == 3" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #10B981;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            <!-- ACH -->
            <svg v-else-if="donation.payment_type_id == 4" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #F59E0B;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            <!-- Fallback -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>

          <!-- Info -->
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
              <span style="font-size: 0.9375rem; font-weight: 700; color: #111827;">
                {{ donorName(donation) }}
              </span>
              <span v-if="donation.is_anonymous" style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #F3F4F6; color: #6B7280; border-radius: 9999px; font-weight: 500;">Anonymous</span>
              <span style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #F3F4F6; color: #6B7280; border-radius: 9999px; font-weight: 500;">#{{ donation.id }}</span>
              <span :style="{ fontSize: '0.625rem', padding: '0.125rem 0.5rem', background: paymentBg(donation.payment_type_id), color: paymentColor(donation.payment_type_id), borderRadius: '9999px', fontWeight: 600 }">
                {{ paymentLabel(donation.payment_type_id) }}
              </span>
              <!-- Wallet type badge -->
              <span v-if="donation.payment_type_id == 1 && donation.wallet_type" style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #EEF2FF; color: #4F46E5; border-radius: 9999px; font-weight: 500;">
                {{ walletLabel(donation.wallet_type) }}
              </span>
            </div>
            <!-- Meta row -->
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-top: 0.25rem; flex-wrap: wrap;">
              <span v-if="donation.business_name" style="font-size: 0.75rem; color: #6B7280;">{{ donation.business_name }}</span>
              <span v-if="donation.campaign_name" style="font-size: 0.75rem; color: #9CA3AF; display: flex; align-items: center; gap: 0.25rem;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.6875rem; height: 0.6875rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                {{ truncate(donation.campaign_name, 35) }}
              </span>
              <span v-if="donation.state" style="font-size: 0.75rem; color: #C4C9D1;">{{ donation.state }}</span>
            </div>
          </div>

          <!-- Amount -->
          <div style="text-align: right; flex-shrink: 0;">
            <div style="font-size: 1.125rem; font-weight: 700; color: #111827;">${{ formatAmount(donation.amount) }}</div>
            <div v-if="donation.paid_for_transaction_fee" style="font-size: 0.625rem; color: #10B981; font-weight: 500;">
              + ${{ formatAmount(donation.calculated_transaction_fee) }} fee covered
            </div>
            <div style="font-size: 0.6875rem; color: #9CA3AF;">{{ formatDate(donation.created_at) }}</div>
          </div>

          <!-- Actions -->
          <div style="flex-shrink: 0;">
            <donation-search-crud-button :user="user" :donation="donation" :key="donation.id"></donation-search-crud-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div v-if="next_page_url" style="text-align: center; margin-top: 1.5rem;">
      <button @click="loadMore" id="loadMore" style="padding: 0.625rem 2rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;" @mouseover="$event.target.style.borderColor='#EC4899'; $event.target.style.color='#EC4899'" @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'">
        Load More ({{ donationsData.to }} of {{ donationsData.total }})
      </button>
    </div>

    <!-- Filter Modal -->
    <transition enter-class="transition ease-out duration-100 transform" enter-active-class="opacity-0" enter-to-class="opacity-100" leave-class="transition ease-in duration-200 transform" leave-active-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="openFilterModal" class="fixed z-50 inset-0 overflow-y-auto" id="donation-search-modal">
        <div class="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true"><div class="absolute inset-0 bg-gray-500 opacity-75"></div></div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div style="border-radius: 0.75rem; max-width: 28rem;" class="inline-block align-bottom bg-white px-6 pt-6 pb-5 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full" role="dialog" aria-modal="true">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem;">
              <h3 style="font-size: 1.125rem; font-weight: 700; color: #111827;">Filter Donations</h3>
              <button @click="openFilterModal = false" style="padding: 0.375rem; color: #9CA3AF; background: none; border: none; cursor: pointer; border-radius: 0.25rem;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style="display: flex; flex-direction: column; gap: 1rem;">
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Donation ID</label>
                <input v-model="donationId" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Campaign ID</label>
                <input v-model="campaignId" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Date Range</label>
                <flat-pickr v-model="date" :config="{mode: 'range'}" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;"></flat-pickr>
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Payment Type</label>
                <select v-model="paymentTypeId" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; background: white;">
                  <option value="">All Types</option>
                  <option value="1">Credit Card</option>
                  <option value="2">Check</option>
                  <option value="3">Cash</option>
                  <option value="4">ACH</option>
                </select>
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">State</label>
                <select v-model="state" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; background: white;">
                  <option value="">All States</option>
                  <option value="AL">Alabama</option><option value="AK">Alaska</option><option value="AZ">Arizona</option><option value="AR">Arkansas</option><option value="CA">California</option><option value="CO">Colorado</option><option value="CT">Connecticut</option><option value="DE">Delaware</option><option value="DC">District of Columbia</option><option value="FL">Florida</option><option value="GA">Georgia</option><option value="HI">Hawaii</option><option value="ID">Idaho</option><option value="IL">Illinois</option><option value="IN">Indiana</option><option value="IA">Iowa</option><option value="KS">Kansas</option><option value="KY">Kentucky</option><option value="LA">Louisiana</option><option value="ME">Maine</option><option value="MD">Maryland</option><option value="MA">Massachusetts</option><option value="MI">Michigan</option><option value="MN">Minnesota</option><option value="MS">Mississippi</option><option value="MO">Missouri</option><option value="MT">Montana</option><option value="NE">Nebraska</option><option value="NV">Nevada</option><option value="NH">New Hampshire</option><option value="NJ">New Jersey</option><option value="NM">New Mexico</option><option value="NY">New York</option><option value="NC">North Carolina</option><option value="ND">North Dakota</option><option value="OH">Ohio</option><option value="OK">Oklahoma</option><option value="OR">Oregon</option><option value="PA">Pennsylvania</option><option value="RI">Rhode Island</option><option value="SC">South Carolina</option><option value="SD">South Dakota</option><option value="TN">Tennessee</option><option value="TX">Texas</option><option value="UT">Utah</option><option value="VT">Vermont</option><option value="VA">Virginia</option><option value="WA">Washington</option><option value="WV">West Virginia</option><option value="WI">Wisconsin</option><option value="WY">Wyoming</option>
                </select>
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Amount ($)</label>
                <input v-model="amount" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Check Number</label>
                <input v-model="checkNumber" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.25rem;">
              <button @click="resetFilters" style="padding: 0.5rem 1rem; background: white; color: #6B7280; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer;">Reset</button>
              <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">Apply Filters</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Export Modal -->
    <div v-if="isExporting || showExportModal" class="fixed inset-0 z-50" style="display: flex; align-items: center; justify-content: center;">
      <div style="position: absolute; inset: 0; background: rgba(0,0,0,0.4);"></div>
      <div style="position: relative; background: white; padding: 1.5rem; border-radius: 0.75rem; box-shadow: 0 20px 25px rgba(0,0,0,0.15); width: 24rem; max-width: 90vw;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
          <h3 style="font-size: 1rem; font-weight: 600; color: #111827;">{{ exportStatus }}</h3>
          <button @click="hideExportModal" style="padding: 0.25rem; color: #9CA3AF; background: none; border: none; cursor: pointer;">
            <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style="width: 100%; height: 0.5rem; background: #F3F4F6; border-radius: 9999px; overflow: hidden; margin-bottom: 0.75rem;">
          <div :style="{ width: exportProgress + '%', height: '100%', background: 'linear-gradient(90deg, #EC4899, #DB2777)', borderRadius: '9999px', transition: 'width 0.3s ease' }"></div>
        </div>
        <p style="font-size: 0.8125rem; color: #6B7280;">{{ exportMessage }}</p>
      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import flatPickr from 'vue-flatpickr-component';

export default {
  props: {
    allDonations: {},
    user: {},
  },
  components: { flatPickr },
  data() {
    return {
      donationsData: this.allDonations,
      donations: this.allDonations?.data,
      openFilterModal: false,
      next_page_url: this.allDonations?.next_page_url ? '/donation-search-with-term?page=2' : '',
      searchTerm: '',
      campaignId: '',
      state: '',
      amount: '',
      checkNumber: '',
      paymentTypeId: '',
      donationId: '',
      date: '',
      filterCount: 0,
      isExporting: false,
      exportStatus: '',
      exportProgress: 0,
      exportMessage: '',
      showExportModal: false,
      exportCheckInterval: null,
    }
  },
  methods: {
    formatDate(date) { return moment(date).format('MM-DD-YYYY'); },
    formatAmount(cents) {
      if (!cents && cents !== 0) return '0';
      return (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.substring(0, len) + '…' : str;
    },
    donorName(d) {
      if (d.first_name) return d.first_name + ' ' + d.last_name;
      if (d.business_name) return d.business_name;
      return 'Anonymous';
    },
    paymentColor(typeId) {
      const map = { 1: '#EC4899', 2: '#3B82F6', 3: '#10B981', 4: '#F59E0B' };
      return map[typeId] || '#9CA3AF';
    },
    paymentBg(typeId) {
      const map = { 1: '#FDF2F8', 2: '#EFF6FF', 3: '#ECFDF5', 4: '#FFFBEB' };
      return map[typeId] || '#F9FAFB';
    },
    paymentLabel(typeId) {
      const map = { 1: 'Card', 2: 'Check', 3: 'Cash', 4: 'ACH' };
      return map[typeId] || 'Other';
    },
    walletLabel(type) {
      const map = { link: 'Stripe Link', google_pay: 'Google Pay', apple_pay: 'Apple Pay' };
      return map[type] || type;
    },
    getFilterRequestQuery() {
      this.filterCount = 0;
      let q = '';
      if (this.amount) { q += '&amount=' + this.amount; this.filterCount++; }
      if (this.state) { q += '&state=' + this.state; this.filterCount++; }
      if (this.paymentTypeId) { q += '&paymentTypeId=' + this.paymentTypeId; this.filterCount++; }
      if (this.donationId) { q += '&donationId=' + this.donationId; this.filterCount++; }
      if (this.date) { q += '&date=' + this.date; this.filterCount++; }
      if (this.checkNumber) { q += '&checkNumber=' + this.checkNumber; this.filterCount++; }
      if (this.campaignId) { q += '&campaignId=' + this.campaignId; this.filterCount++; }
      return q;
    },
    resetFilters() {
      this.campaignId = ''; this.state = ''; this.amount = '';
      this.checkNumber = ''; this.paymentTypeId = ''; this.donationId = ''; this.date = '';
    },
    search() {
      axios.get('/donation-search-with-term?search_term=' + this.searchTerm + this.getFilterRequestQuery())
        .then(response => {
          this.donations = response.data.data;
          this.donationsData = response.data;
          this.next_page_url = response.data.next_page_url;
          this.openFilterModal = false;
        });
    },
    loadMore() {
      if (this.next_page_url) {
        axios.get(this.next_page_url + '&search_term=' + this.searchTerm).then(response => {
          this.donationsData = response.data;
          response.data.data.forEach(v => this.donations.push(v));
          this.next_page_url = response.data.next_page_url;
        });
      }
    },
    download() {
      this.isExporting = true; this.showExportModal = true;
      this.exportStatus = 'Starting export...'; this.exportProgress = 0;
      axios.post('/donation-search-export?search_term=' + this.searchTerm + this.getFilterRequestQuery(), {
        file_name: 'donations-export.xlsx'
      }).then(() => this.checkExportStatus()).catch(() => {
        this.isExporting = false; this.exportStatus = 'Export failed.';
      });
    },
    hideExportModal() {
      this.showExportModal = false; this.exportProgress = 0;
      this.exportMessage = ''; this.exportStatus = ''; this.isExporting = false;
      if (this.exportCheckInterval) { clearInterval(this.exportCheckInterval); this.exportCheckInterval = null; }
    },
    checkExportStatus() {
      axios.get('/donation-export-status').then(response => {
        if (response.data.status === 'completed') {
          this.isExporting = false; this.exportStatus = 'Export completed!'; this.exportProgress = 100;
          window.location.href = response.data.download_url;
          setTimeout(() => this.hideExportModal(), 2000);
        } else if (response.data.status === 'pending' || response.data.status === 'queued') {
          this.exportStatus = response.data.message || 'Processing...';
          this.exportProgress = response.data.progress || 0;
          this.exportMessage = response.data.message || '';
          setTimeout(() => this.checkExportStatus(), 1000);
        } else {
          this.isExporting = false; this.exportStatus = 'Export failed.'; this.exportProgress = 0;
        }
      }).catch(() => { this.isExporting = false; this.exportStatus = 'Failed.'; });
    },
  },
  beforeUnmount() {
    if (this.exportCheckInterval) clearInterval(this.exportCheckInterval);
  },
}
</script>
